import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useScroll } from '@use-gesture/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { useDimensionObserver } from '@micromag/core/hooks';

var styles = {"container":"micromag-element-scroll-container","withScroll":"micromag-element-scroll-withScroll","scrollable":"micromag-element-scroll-scrollable","top":"micromag-element-scroll-top","scrollee":"micromag-element-scroll-scrollee","middle":"micromag-element-scroll-middle","bottom":"micromag-element-scroll-bottom","withArrow":"micromag-element-scroll-withArrow","arrowContainer":"micromag-element-scroll-arrowContainer","withShadow":"micromag-element-scroll-withShadow","arrow":"micromag-element-scroll-arrow","upAndDown":"micromag-element-scroll-upAndDown"};

var propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  disabled: PropTypes.bool,
  verticalAlign: PropTypes.oneOf(['top', 'middle', 'bottom']),
  className: PropTypes.string,
  scrollableClassName: PropTypes.string,
  scrolleeClassName: PropTypes.string,
  children: PropTypes.node,
  scrollPosition: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onScrolledBottom: PropTypes.func,
  onScrolledNotBottom: PropTypes.func,
  onScrollHeightChange: PropTypes.func,
  scrollContainerRef: PropTypes.any,
  // eslint-disable-line
  withShadow: PropTypes.bool,
  withArrow: PropTypes.bool
};
var defaultProps = {
  width: null,
  height: null,
  disabled: false,
  verticalAlign: null,
  className: null,
  scrollableClassName: null,
  scrolleeClassName: null,
  children: null,
  scrollPosition: null,
  onScrolledBottom: null,
  onScrolledNotBottom: null,
  onScrollHeightChange: null,
  scrollContainerRef: null,
  withShadow: false,
  withArrow: true
};
function Scroll(_ref) {
  var width = _ref.width,
    height = _ref.height,
    disabled = _ref.disabled,
    verticalAlign = _ref.verticalAlign,
    className = _ref.className,
    scrollableClassName = _ref.scrollableClassName,
    scrolleeClassName = _ref.scrolleeClassName,
    children = _ref.children,
    scrollPosition = _ref.scrollPosition,
    onScrolledBottom = _ref.onScrolledBottom,
    onScrolledNotBottom = _ref.onScrolledNotBottom,
    onScrollHeightChange = _ref.onScrollHeightChange,
    scrollContainerRef = _ref.scrollContainerRef,
    withShadow = _ref.withShadow,
    showArrow = _ref.withArrow;
  var finalStyle = {
    width: width,
    height: height
  };
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    withArrow = _useState2[0],
    setWithArrow = _useState2[1];
  var _useDimensionObserver = useDimensionObserver(),
    scrollableRef = _useDimensionObserver.ref,
    scrollableHeight = _useDimensionObserver.height;
  if (scrollContainerRef !== null) {
    scrollContainerRef.current = scrollableRef.current; // eslint-disable-line
  }
  var _useDimensionObserver2 = useDimensionObserver(),
    scrolleeRef = _useDimensionObserver2.ref,
    scrolleeHeight = _useDimensionObserver2.height;
  var scrolledBottomOnce = useRef(false);
  var scrolledNotBottomOnce = useRef(false);
  var reachedBottom = useRef(false);
  var bind = useScroll(function (_ref2) {
    var _ref2$xy = _slicedToArray(_ref2.xy, 2),
      scrollY = _ref2$xy[1];
    var newWithArrow = scrollY <= 1;
    var maxScrollAmount = scrolleeHeight - scrollableHeight;
    var nowReachedBottom = scrollY + 1 >= maxScrollAmount;
    if (nowReachedBottom) {
      if (!reachedBottom.current) {
        if (onScrolledBottom !== null) {
          onScrolledBottom({
            initial: !scrolledBottomOnce.current
          });
        }
        scrolledBottomOnce.current = true;
      }
    } else if (reachedBottom.current) {
      if (onScrolledNotBottom !== null) {
        onScrolledNotBottom({
          initial: !scrolledNotBottomOnce.current
        });
      }
      scrolledNotBottomOnce.current = true;
    }
    if (newWithArrow !== withArrow && showArrow) {
      setWithArrow(newWithArrow);
    }
    reachedBottom.current = nowReachedBottom;
  }, {
    enabled: !disabled,
    threshold: 10,
    axis: 'y',
    filterTaps: true
  });

  // need to call scrolled callbacks on initial render also

  useEffect(function () {
    if (scrolleeHeight > 0 && scrollableHeight > 0 && !disabled) {
      setWithArrow(Math.round(scrolleeHeight) > Math.round(scrollableHeight));
      var maxScrollAmount = scrolleeHeight - scrollableHeight;
      var nowReachedBottom = scrollableRef.current.scrollTop + 1 >= maxScrollAmount;
      if (nowReachedBottom) {
        if (onScrolledBottom !== null) {
          onScrolledBottom({
            initial: false
          });
        }
      } else if (onScrolledNotBottom !== null) {
        onScrolledNotBottom({
          initial: false
        });
      }
    }
  }, [scrollableHeight, scrolleeHeight, setWithArrow, disabled]);
  useEffect(function () {
    if (onScrollHeightChange !== null) {
      var canScroll = (scrolleeHeight || 0) > (scrollableHeight || 0);
      onScrollHeightChange({
        scrollableHeight: scrollableHeight,
        scrolleeHeight: scrolleeHeight,
        canScroll: canScroll
      });
    }
  }, [scrollableHeight, scrolleeHeight, onScrollHeightChange]);
  useEffect(function () {
    if (scrollableRef.current !== null && scrollPosition !== null) {
      scrollableRef.current.scrollTop = scrollPosition;
    }
  }, [scrolleeRef, scrollPosition]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, styles.withScroll, !disabled), className, className !== null), styles[verticalAlign], verticalAlign !== null && scrolleeHeight < scrollableHeight), styles.withArrow, showArrow && withArrow), styles.withShadow, withShadow)]),
    style: finalStyle
  }, /*#__PURE__*/React.createElement("div", Object.assign({
    className: classNames([styles.scrollable, _defineProperty({}, scrollableClassName, scrollableClassName !== null)]),
    ref: scrollableRef
  }, bind()), /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.scrollee, _defineProperty({}, scrolleeClassName, scrolleeClassName !== null)]),
    ref: scrolleeRef
  }, children)), !disabled && showArrow ? /*#__PURE__*/React.createElement("div", {
    className: styles.arrowContainer
  }, /*#__PURE__*/React.createElement("svg", {
    xmlns: "http://www.w3.org/2000/svg",
    width: "10",
    height: "16",
    viewBox: "0 0 10 16",
    className: styles.arrow,
    fill: "currentColor"
  }, /*#__PURE__*/React.createElement("polygon", {
    // stroke="#000"
    // strokeWidth="0.5"
    points: ".38 11.38 5 16 9.62 11.38 8.56 10.32 5.75 13.13 5.75 1.61 4.25 1.61 4.25 13.13 1.44 10.32 .38 11.38"
  }))) : null);
}
Scroll.defaultProps = defaultProps;
Scroll.propTypes = propTypes;
var Scroll$1 = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Scroll, Object.assign({
    scrollContainerRef: ref
  }, props));
});

export { Scroll$1 as default };
